import React, { useCallback, useEffect, useRef, useState } from "react";

import { v4 as uuidv4 } from "uuid";

import { isNonEmptyString, isNonEmptyObject } from "@cayuse/shared-react.utils.type-checks";

import ConfirmationModal from "components/ConfirmationModal";
import { doDeleteElement, doMoveElement } from "containers/Form/SimplifiedBuilder/Components/PanelComponent/utils";
import DebouncedComponent from "containers/Form/SimplifiedBuilder/Internal/DebouncedComponent";
import DraggableItem from "containers/Form/SimplifiedBuilder/Internal/DraggableItem";
import {
	cleanupFormDefinition,
	isPlaceholderRow,
	removePlaceholdersFromRows,
} from "containers/Form/SimplifiedBuilder/Internal/ModelFunctions";
import { useDictionary } from "hooks";

import PanelHeaderComponent from "./PanelHeaderComponent";

const getEmptyElement = () => ({
	id: uuidv4(),
	cols: [
		{
			id: uuidv4(),
			type: "",
			placeholder: true,
		},
	],
});

function PanelComponent (props) {
	const { completeFormModel, config, onChange, id, setOnPaletteDropEndFunction, currentTab } = props;
	const element = useRef(null);
	const dictionary = useDictionary("components.panelComponent");
	const [ shouldSave, setShouldSave ] = useState(false);
	const [ deleteState, setDeleteState ] = useState({
		element: {},
		shouldConfirm: false,
		isConfirmed: false,
	});

	const [ layoutDefinition, setLayoutDefinition ] = useState(() => {
		const enhancedElements = [];

		for (let row of config.extraSettings.layoutDefinition.rows) {
			enhancedElements.push(getEmptyElement());
			enhancedElements.push({ ...row, id: uuidv4() });
		}
		if (enhancedElements.length === 0) {
			return [ getEmptyElement() ];
		}

		return enhancedElements;
	});

	useEffect(() => {
		const [ rows, didChange ] = removePlaceholdersFromRows(config.extraSettings.layoutDefinition.rows || []);
		if (didChange) {
			config.extraSettings.layoutDefinition.rows = rows;
			onChange(config);
		}
	}, [ config, onChange ]);
	useEffect(() => {
		// fixes issue of layoutDefinition not updating when the first section is selected and deleted
		if (currentTab === 0) {
			const newRows = config.extraSettings.layoutDefinition.rows.map(row => ({ ...row, id: row?.id || uuidv4() }));
			setLayoutDefinition(cleanupFormDefinition(newRows));
		}
	}, [ config.extraSettings.layoutDefinition, currentTab ]);

	useEffect(() => {
		// handle save
		if (shouldSave) {
			setShouldSave(false);
			const newConfig = { ...config };
			const [ rows ] = removePlaceholdersFromRows(layoutDefinition);
			newConfig.extraSettings.layoutDefinition.rows = rows;

			onChange(newConfig);
		}
	}, [ config, layoutDefinition, onChange, shouldSave ]);

	useEffect(() => {
		// handle deletion
		const { isConfirmed, shouldConfirm, element: elementToDelete } = deleteState;
		if (isNonEmptyObject(elementToDelete) && (!shouldConfirm || isConfirmed)) {
			setLayoutDefinition(prevLayoutDefinition => cleanupFormDefinition(
				doDeleteElement(prevLayoutDefinition, elementToDelete),
			));
			setShouldSave(true);

			resetDelete();
		}
	}, [ deleteState ]);

	const onConfirmDelete = () => setDeleteState(prev => ({ ...prev, isConfirmed: true }));

	const resetDelete = () => setDeleteState({ element: null, shouldConfirm: false, isConfirmed: false });

	const onDeleteClick = useCallback((row, col) => {
		const element = { row: row, column: col };
		const formElement = layoutDefinition[row].cols[col];
		const displayRules = formElement.visible;
		setDeleteState({
			element,
			shouldConfirm: isNonEmptyString(displayRules),
			isConfirmed: false,
		});
	}, [ layoutDefinition ]);


	const onChangePanelContent = useCallback((r, c, content) => {
		setLayoutDefinition(layoutDefinition => {
			layoutDefinition[r].cols[c] = content;

			return layoutDefinition;
		});
		setShouldSave(true);
	}, []);

	const moveComponent = useCallback(
		(dragRow, dragCol, hoverRow, newColPosition) => setLayoutDefinition(
			prev => doMoveElement(prev, dragRow, dragCol, hoverRow, newColPosition)
		), []
	);

	const insertNewComponent = useCallback((dragComponent, hoverRow, newColPosition) =>
		setLayoutDefinition(layoutDefinition => {
			const newLayoutDefinition = [ ...layoutDefinition ];
			newLayoutDefinition[hoverRow].cols.splice(newColPosition, 0, dragComponent);

			return newLayoutDefinition;
		}), []);

	const getParentBoundingBox = useCallback(
		() => element.current?.getBoundingClientRect(),
		[],
	);

	const getNumberOfElementsInRow = useCallback(
		row => isPlaceholderRow(layoutDefinition, row)
			? 0
			: layoutDefinition[row].cols.length,
		[ layoutDefinition ]);

	const onDrop = useCallback(() => {
		setLayoutDefinition(prev => cleanupFormDefinition(prev));
		setShouldSave(true);
	}, []);

	useEffect(() => {
		setOnPaletteDropEndFunction({
			end () {
				onDrop();
			},
		});
	}, [ // eslint-disable-line react-hooks/exhaustive-deps
		setOnPaletteDropEndFunction,
	]);

	return (
		<div role="tabpanel" id={id} ref={element} key={id} style={{ display: "flex", flexDirection: "column" }}>
			<PanelHeaderComponent completeFormModel={completeFormModel} config={config} onChange={onChange} />
			{layoutDefinition && layoutDefinition.map((row, r) => (
				<div style={{ display: "flex", flex: "1 0 auto" }} key={row.id}>
					{row.cols.map((col, c) => (
						<DraggableItem
							key={col.id}
							id={col.id}
							row={r}
							col={c}
							item={col}
							moveComponent={moveComponent}
							insertNewComponent={insertNewComponent}
							getParentBoundingBox={getParentBoundingBox}
							getNumberOfElementsInRow={getNumberOfElementsInRow}
							onDrop={onDrop}
							isPlaceholder={col.placeholder}
							pageIsEmpty={r === 0 && c === 0 && layoutDefinition.length === 1}
						>
							<DebouncedComponent
								{...props}
								id={`${id}.extraSettings.layoutDefinition.rows[${r}].cols[${c}]`}
								config={col}
								onChange={content => onChangePanelContent(r, c, content)}
								onDelete={() => onDeleteClick(r, c)}
							/>
						</DraggableItem>
					))}
				</div>
			))}
			{deleteState.shouldConfirm && !deleteState.isConfirmed && (
				<ConfirmationModal
					cancelButtonLabel={dictionary.modal.deleteQuestion.cancelButtonLabel}
					confirmButtonLabel={dictionary.modal.deleteQuestion.confirmButtonLabel}
					icon="file-alt"
					message={dictionary.modal.deleteQuestion.message}
					onCancel={resetDelete}
					title={dictionary.modal.deleteQuestion.title}
					visible={true}
					onConfirm={onConfirmDelete}
				/>
			)}
		</div>
	);
}

PanelComponent.showInPalette = () => false;

export default PanelComponent;
